import { AfterViewInit, ChangeDetectorRef, Component, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { IonIcon } from '@ionic/angular/standalone';
import ObjectID from 'bson-objectid';
import * as lodash from 'lodash';
import { DateTime } from 'luxon';
import { firstValueFrom, Observable, of } from 'rxjs';
import { ButtonClickDirective } from 'src/app/directives/buttons/button-click-action/button-click.directive';
import { MaterialImportsModule } from 'src/app/material-imports.module';
import { ITask, IUserSetting } from 'src/app/model/thingie/task';
import { IUserTemplate } from 'src/app/model/thingie/templates';
import { UserDataService } from 'src/app/services/user-data/user-data.service';
import { UserService } from 'src/app/services/user/user.service';
import { deepCopyFrozenSync, deepCopySync } from 'src/app/utility/deep-copy';
import { createNameTakenValidator } from 'src/app/utility/validators/name-taken-validator';
import { SubSink } from 'subsink';
import { SplitButtonComponent } from '../../buttons/split-button/split-button.component';
import { DialogControl } from '../../dialogs/dialog/dialog-control';
import { IIsDialog } from '../../dialogs/dialog/dialog.service';
import { LisProfileGeneratorComponent } from '../lis-profile-generator/lis-profile-generator.component';

export interface IDialogArgs {
  readonly task: ITask;

  /**
   * Represents the input in 'task'
   * as ````
   * const input = task.inputs[inputKey];
   * ````
   */
  readonly inputKey: string;

  readonly templateName: string;

  /**
   * Whether the profile is editable
   */
  readonly readonly: boolean;
}

export interface IDialogResult {
  profile: any[][];

  /**
   * Profile ID
   *
   * Creation mode:
   * always the ID of the newly-created configuration/template.
   *
   * Edit mode:
   * - no changes made: undefined
   * - changes made: special value @see DataProcessingService
   */
  profileId?: string;

  /**
   * Name of the feeding profile.
   *
   * When the profile is unchanged, contains
   * the profile name that was provided to the dialog.
   *
   * If a new named profile was created,
   * contains the name of the named profile.
   *
   * If the custom profile is used, contains the
   * special string identifying a custom profile.
   */
  profileName: string;
}

@Component({
  templateUrl: './feeding-profile-config-dialog.component.html',
  styleUrls: ['./feeding-profile-config-dialog.component.scss'],
  imports: [
    MaterialImportsModule,
    LisProfileGeneratorComponent,
    IonIcon,
    SplitButtonComponent,
    FormsModule,
    ReactiveFormsModule,
    ButtonClickDirective,
  ]
})
export class FeedingProfileConfigDialogComponent implements OnInit, AfterViewInit, OnDestroy, IIsDialog<IDialogArgs, IDialogResult | string | undefined> {
  public readonly __compileTimeTypeInferenceHelper: [IDialogArgs, string | IDialogResult | undefined][] = [];

  @ViewChild('buttonTemplate', { read: TemplateRef<unknown> })
  private set buttonTemplate(template: TemplateRef<unknown>) {
    this.dialogControl.setActionButtonsTemplate(template);
  }

  @ViewChild(LisProfileGeneratorComponent)
  public _lisProfileGenerator!: LisProfileGeneratorComponent;

  protected readonly templateName: string;

  protected readonly dialogOptions: IDialogArgs;

  protected newProfileNameControl = new FormControl();

  protected askForCopyName = false;
  protected profileNameErrorMessage = '';

  private feedingProfile: any[][];
  private subscriptions = new SubSink();

  public get hasValidProfile(): boolean {
    return this._lisProfileGenerator && this._lisProfileGenerator.valid;
  }

  public readonly title$: Observable<string>;

  constructor(
    private changeDetector: ChangeDetectorRef,
    private dialogControl: DialogControl<IDialogArgs, IDialogResult>,
    private userData: UserDataService,
    private userService: UserService,
  ) {
    this.dialogOptions = deepCopyFrozenSync(dialogControl.data);
    const input = this.dialogOptions.task.inputs[this.dialogOptions.inputKey]!;

    if (
      input.source !== 'usersetting'
      || (input as IUserSetting).inputElementType !== 'lisprofile-generator'
    ) {
      throw new Error('Invalid input provided. Only user-setting inputs '
        + 'with type `lisprofile-generator` are supported.');
    }

    this.feedingProfile = input.value as any[][];
    this.templateName = this.dialogOptions.templateName === UserDataService.customTemplateIndicator
      ? 'Custom Profile'
      : this.dialogOptions.templateName;

    this.title$ = of(`Feeding profile '${this.templateName}'`);
  }

  public async ngOnInit() {
    const userTemplateList = await this.userData.getAllTemplates();

    this.newProfileNameControl = new FormControl(
      '',
      {
        validators: [
          Validators.required,
          Validators.pattern(/^\w/)
        ],
        asyncValidators: [
          createNameTakenValidator(
            of(userTemplateList.map(template => ({ _id: template._id, name: template.template.name }))),
            () => '',
            'Profile name {} is already taken.'
          ),
        ]
      }
    );

    this.subscriptions.sink = this.newProfileNameControl
      .statusChanges
      .subscribe(
        status => {
          if (status === 'INVALID') {
            this.profileNameErrorMessage = this.generateProfileNameErrorMessage();
          }
        }
      );
  }

  public ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  public ngAfterViewInit(): void {
    // Required, because during the change-detection chain,
    // 'this.lisProfileGenerator' starts out as undefined
    // but might change to holding a component in the end.
    this.changeDetector.detectChanges();
  }

  protected _onFeedingProfileUpdate(profile: any[][]) {
    this.feedingProfile = profile;
  }

  protected _addRow() {
    this._lisProfileGenerator.addNewSegment();
  }

  protected _closeAndReturnProfile() {
    const hasChanges = this.hasProfileChanged(this.feedingProfile);

    const profileName = hasChanges
      ? UserDataService.customTemplateIndicator
      : this.dialogOptions.templateName;

    const dialogResult: IDialogResult = {
      profileId: hasChanges ? UserDataService.customTemplateIndicator : undefined,
      profileName,
      profile: this.feedingProfile
    };

    void this.dialogControl.requestCloseDialog(dialogResult);
  }

  protected _saveAsCopy() {
    this.askForCopyName = true;
  }

  protected _stopProfileCopyCreation() {
    this.askForCopyName = false;
  }

  protected async _createProfileCopyAndClose() {
    const name = this.newProfileNameControl.value;
    const input = deepCopySync(this.dialogOptions.task.inputs[this.dialogOptions.inputKey]) as IUserSetting;

    if (name === null) {
      return;
    }

    input.value = this.feedingProfile;

    const profileEntry: IUserTemplate = {
      _id: (new ObjectID()).toHexString(),
      type: 'userSettingTemplate',
      version: 'userDefined',
      created: DateTime.now().toISO(),
      template: {
        name,
        type: 'userSettingTemplate',
        payload: input,
      },
    };
    //Get current userID
    const currentUser = await this.userService.getCurrentUser();
    if (currentUser === undefined || currentUser._id === undefined) {
      //This will probably never happen
      return;
    }

    const result = await firstValueFrom(this.userData.createUserData('LIS_PROFILE', currentUser._id, profileEntry, 'PUBLIC'));

    await this.dialogControl.requestCloseDialog({
      profileId: result.value._id,
      profileName: name,
      profile: this.feedingProfile
    });
  }

  private generateProfileNameErrorMessage() {
    const errors: string[] = [];

    if (this.newProfileNameControl.hasError('required')) {
      errors.push('Name required');
    }

    if (this.newProfileNameControl.hasError('nameTaken')) {
      errors.push('Name already taken.');
    }

    if (this.newProfileNameControl.hasError('pattern')) {
      errors.push('Name needs to start with a letter or number');
    }

    return errors.join(';');
  }

  private hasProfileChanged(profileToCompare: any[][]) {
    const originalProfile = (this.dialogOptions.task.inputs[this.dialogOptions.inputKey] as IUserSetting).value as any[][];

    return !lodash.isEqual(profileToCompare, originalProfile);
  }

  protected closeDialog() {
    void this.dialogControl.requestCloseDialog(undefined);
  }
}
